// src/pages/maintenance/MaintenanceAssetsPage.js
// Asset list — filter by location/category/status, create/edit, QR labels (single + batch),
// archive. See docs/MAINTENANCE_MODULE.md.
import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import maintenanceService from '../../services/maintenanceService';
import ConfirmModal from '../../components/ConfirmModal';
import SearchableSelect from '../../components/SearchableSelect';
import MaintenanceNav from '../../components/MaintenanceNav';
import AssetModal from './AssetModal';
import QuickMaintenanceStatusModal from './QuickMaintenanceStatusModal';

const STATUS_BADGE = {
  operational: 'success',
  under_maintenance: 'warning',
  out_of_service: 'danger',
  decommissioned: 'secondary',
};

const labelize = (s) => (s || '').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());

function downloadBlob(blob, filename) {
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  window.URL.revokeObjectURL(url);
}

export default function MaintenanceAssetsPage() {
  const [assets, setAssets] = useState([]);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filters, setFilters] = useState({ locationId: '', category: '', status: '', search: '' });
  const [showModal, setShowModal] = useState(false);
  const [editingAsset, setEditingAsset] = useState(null);
  const [archiveTarget, setArchiveTarget] = useState(null);
  const [selected, setSelected] = useState([]);
  const [quickMaintTarget, setQuickMaintTarget] = useState(null);
  const [returnTarget, setReturnTarget] = useState(null);
  const [returning, setReturning] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v; });
      const [assetList, locationList] = await Promise.all([
        maintenanceService.getAssets(params),
        maintenanceService.getLocations(),
      ]);
      setAssets(assetList);
      setLocations(locationList);
    } catch (err) {
      setError('Failed to load assets.');
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => { load(); }, [load]);

  const handleSave = () => { setShowModal(false); setEditingAsset(null); load(); };

  const handleArchive = async () => {
    try {
      await maintenanceService.deleteAsset(archiveTarget.id);
      setArchiveTarget(null);
      load();
    } catch {
      setError('Failed to archive asset.');
    }
  };

  const handleReturnToOperational = async () => {
    setReturning(true);
    try {
      await maintenanceService.updateAsset(returnTarget.id, { status: 'operational' });
      setReturnTarget(null);
      load();
    } catch {
      setError('Failed to update status.');
    } finally {
      setReturning(false);
    }
  };

  const toggleSelected = (id) => {
    setSelected(s => s.includes(id) ? s.filter(x => x !== id) : [...s, id]);
  };

  const downloadLabel = async (asset) => {
    const blob = await maintenanceService.downloadAssetQrLabel(asset.id);
    downloadBlob(blob, `asset-${asset.id}-qr.pdf`);
  };

  const downloadBatch = async () => {
    if (!selected.length) return;
    const blob = await maintenanceService.downloadBatchQrLabels(selected);
    downloadBlob(blob, 'asset-qr-labels.pdf');
  };

  return (
    <>
      <MaintenanceNav />
      <div className="container-fluid py-3">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h4 className="mb-0">Maintenance Assets</h4>
        <div className="d-flex gap-2">
          {selected.length > 0 && (
            <button className="btn btn-outline-secondary btn-sm" onClick={downloadBatch}>
              Print {selected.length} QR Label{selected.length > 1 ? 's' : ''}
            </button>
          )}
          <Link to="/maintenance/scan" className="btn btn-outline-secondary btn-sm">Scan QR</Link>
          <button className="btn btn-primary btn-sm" onClick={() => { setEditingAsset(null); setShowModal(true); }}>+ New Asset</button>
        </div>
      </div>

      {error && <div className="alert alert-danger py-2" style={{ fontSize: 13 }}>{error}</div>}

      <div className="row g-2 mb-3">
        <div className="col-md-3">
          <input className="form-control form-control-sm" placeholder="Search by name…" value={filters.search} onChange={e => setFilters(f => ({ ...f, search: e.target.value }))} />
        </div>
        <div className="col-md-3">
          <SearchableSelect
            options={locations.map(l => ({ value: l.id, label: l.name }))}
            value={filters.locationId}
            onChange={val => setFilters(f => ({ ...f, locationId: val || '' }))}
            placeholder="All locations"
            size="sm"
          />
        </div>
        <div className="col-md-3">
          <SearchableSelect
            options={['operational', 'under_maintenance', 'out_of_service', 'decommissioned'].map(s => ({ value: s, label: labelize(s) }))}
            value={filters.status}
            onChange={val => setFilters(f => ({ ...f, status: val || '' }))}
            placeholder="All statuses"
            size="sm"
          />
        </div>
      </div>

      {loading ? (
        <div className="text-center py-5 text-muted">Loading…</div>
      ) : assets.length === 0 ? (
        <div className="text-center py-5 text-muted">No assets found.</div>
      ) : (
        <div className="table-responsive">
          <table className="table table-sm table-hover align-middle">
            <thead>
              <tr>
                <th style={{ width: 32 }}></th>
                <th>Name</th>
                <th>Category</th>
                <th>Location</th>
                <th>Status</th>
                <th>Criticality</th>
                <th title="Manual uploaded">📖</th>
                <th>Next PM Due</th>
                <th style={{ width: 1 }}></th>
              </tr>
            </thead>
            <tbody>
              {assets.map(a => (
                <tr key={a.id}>
                  <td>
                    <input type="checkbox" checked={selected.includes(a.id)} onChange={() => toggleSelected(a.id)} />
                  </td>
                  <td>
                    <Link to={`/maintenance/assets/${a.id}`} style={{ fontWeight: 600, fontSize: 13 }}>{a.name}</Link>
                    {a.isFoodSafetyCritical && <span className="badge bg-info-subtle text-info ms-2" style={{ fontSize: 10 }}>Food Safety</span>}
                  </td>
                  <td style={{ fontSize: 13 }}>{labelize(a.category)}</td>
                  <td style={{ fontSize: 13 }}>{a.Location?.name || '—'}</td>
                  <td><span className={`badge bg-${STATUS_BADGE[a.status] || 'secondary'}`}>{labelize(a.status)}</span></td>
                  <td style={{ fontSize: 13 }}>{labelize(a.criticalityLevel)}</td>
                  <td className="text-center">{a.manualFileId ? <span className="text-success">✓</span> : <span className="text-muted">—</span>}</td>
                  <td style={{ fontSize: 13 }}>{a.nextPmDueAt ? new Date(a.nextPmDueAt).toLocaleDateString() : '—'}</td>
                  <td style={{ whiteSpace: 'nowrap' }}>
                    <div className="d-flex justify-content-end gap-1">
                      {['under_maintenance', 'out_of_service'].includes(a.status) ? (
                        <button className="btn btn-sm btn-outline-success" title="Mark Back in Operation" onClick={() => setReturnTarget(a)}>✓</button>
                      ) : a.status !== 'decommissioned' && (
                        <button className="btn btn-sm btn-outline-warning" title="Mark Under Maintenance" onClick={() => setQuickMaintTarget(a)}>🔧</button>
                      )}
                      <button className="btn btn-sm btn-outline-secondary" onClick={() => downloadLabel(a)}>QR Label</button>
                      <button className="btn btn-sm btn-outline-primary" onClick={() => { setEditingAsset(a); setShowModal(true); }}>Edit</button>
                      <button className="btn btn-sm btn-outline-danger" onClick={() => setArchiveTarget(a)}>Archive</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showModal && (
        <AssetModal
          asset={editingAsset}
          locations={locations}
          onSave={handleSave}
          onClose={() => { setShowModal(false); setEditingAsset(null); }}
        />
      )}

      <ConfirmModal
        show={!!archiveTarget}
        title="Archive Asset"
        message={`Archive "${archiveTarget?.name}"? It will be hidden from active lists but kept for historical work orders.`}
        confirmLabel="Archive"
        onConfirm={handleArchive}
        onCancel={() => setArchiveTarget(null)}
      />

      {quickMaintTarget && (
        <QuickMaintenanceStatusModal
          asset={quickMaintTarget}
          onSave={() => { setQuickMaintTarget(null); load(); }}
          onClose={() => setQuickMaintTarget(null)}
        />
      )}

      <ConfirmModal
        show={!!returnTarget}
        title="Back in Operation"
        message={`Mark "${returnTarget?.name}" back as Operational? New temperature alarms will resume requiring normal review from now on.`}
        confirmLabel={returning ? 'Saving…' : 'Mark Operational'}
        variant="success"
        onConfirm={handleReturnToOperational}
        onCancel={() => setReturnTarget(null)}
      />
      </div>
    </>
  );
}
